'use client';
import React, { useEffect, useState, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { Document, Category } from '@/lib/types';
import { usePublicStore } from './publicStore';
import DocumentCard from './DocumentCard';
import RecentlyUpdated from './RecentlyUpdated';
import { FileSearch } from 'lucide-react';

export default function DocumentGrid() {
  const { searchQuery, activeCategory } = usePublicStore();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();

    const fetchData = async () => {
      setLoading(true);
      try {
        const [docsRes, catsRes] = await Promise.all([
          supabase
            .from('documents')
            .select('*, category:categories(id, name, is_active, icon, description, display_order, slug, created_at, updated_at)')
            .eq('status', 'Published')
            .order('updated_at', { ascending: false }),
          supabase
            .from('categories')
            .select('*')
            .eq('is_active', true)
            .order('display_order', { ascending: true }),
        ]);

        if (docsRes.data) {
          // Filter to only docs in active categories
          const activeCatIds = new Set((catsRes.data || []).map((c: any) => c.id));
          const filtered = docsRes.data.filter((d: any) => activeCatIds.has(d.category_id));
          setDocuments(filtered as Document[]);
        }
        if (catsRes.data) setCategories(catsRes.data as Category[]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredDocs = useMemo(() => {
    let docs = [...documents];

    if (activeCategory !== 'all') {
      docs = docs.filter((d) => d.category_id === activeCategory);
    }

    if (searchQuery?.trim()) {
      const q = searchQuery.toLowerCase().trim();
      docs = docs.filter(
        (d) =>
          d.title?.toLowerCase().includes(q) ||
          d.description?.toLowerCase().includes(q) ||
          d.keywords?.some((k) => k?.toLowerCase().includes(q)) ||
          (d.category as any)?.name?.toLowerCase().includes(q)
      );
    }

    return docs;
  }, [documents, activeCategory, searchQuery]);

  const recentDocs = useMemo(() => {
    const cutoff = new Date(Date.now() - 8 * 24 * 60 * 60 * 1000);
    return documents
      .filter((d) => new Date(d.updated_at) >= cutoff)
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 4);
  }, [documents]);

  const activeCatName =
    activeCategory === 'all' ?'All Forms'
      : categories.find((c) => c.id === activeCategory)?.name ?? 'Forms';

  if (loading) {
    return (
      <section className="py-6 px-4 sm:py-8">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`skel-${i}`} className="card p-4 sm:p-5 animate-pulse">
                <div className="w-10 h-10 rounded-lg bg-muted mb-3" />
                <div className="h-4 bg-muted rounded mb-2 w-3/4" />
                <div className="h-3 bg-muted rounded mb-1 w-full" />
                <div className="h-3 bg-muted rounded w-2/3" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-6 px-4 sm:py-8">
      <div className="max-w-screen-2xl mx-auto">
        {/* Recently Updated Strip — only show when no active search/filter */}
        {!searchQuery && activeCategory === 'all' && recentDocs.length > 0 && (
          <RecentlyUpdated docs={recentDocs} categories={categories} />
        )}

        {/* Section header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-foreground">{activeCatName}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {filteredDocs.length} form{filteredDocs.length !== 1 ? 's' : ''} available
              {searchQuery && (
                <span className="ml-1">
                  matching &ldquo;<span className="font-semibold text-primary">{searchQuery}</span>&rdquo;
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Empty state */}
        {filteredDocs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 sm:py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <FileSearch size={28} className="text-muted-foreground" />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-2">
              {searchQuery ? 'No matching forms found' : 'No forms available in this category'}
            </h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              {searchQuery
                ? 'Try a different search term, or browse by category above.'
                : 'This category currently has no published forms. Check back later or contact the college office.'}
            </p>
          </div>
        )}

        {/* Document grid */}
        {filteredDocs.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4 sm:gap-5">
            {filteredDocs.map((doc) => (
              <DocumentCard key={`doc-card-${doc.id}`} document={doc} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}